import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import { useEffect, useRef, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";

import { Sidebar } from "./components/Sidebar";
import { Topbar } from "./components/Topbar";

import { Dashboard } from "./screens/Dashboard";
import { Quickstart } from "./screens/Quickstart";
import { Logs } from "./screens/Logs";
import { Users } from "./screens/Users";
import { Keys } from "./screens/Keys";
import { Policy } from "./screens/Policy";
import { SigninPage } from "./screens/SigninPage";
import { Team } from "./screens/Team";
import { Billing } from "./screens/Billing";
import { Settings } from "./screens/Settings";
import { Login } from "./screens/Login";
import { Welcome } from "./screens/Welcome";
import { NewOrganization } from "./screens/NewOrganization";
import { Invitation } from "./screens/Invitation";

import { Survey } from "./screens/onboarding/Survey";
import { Explainer } from "./screens/onboarding/Explainer";
import { GenerateKey } from "./screens/onboarding/GenerateKey";
import { Done as OnboardingDone } from "./screens/onboarding/Done";

import { getSession } from "./lib/auth";
import { apiClient, type OnboardingStep } from "./lib/api";

const SCREEN_LABEL: Record<string, string> = {
  "/": "dashboard",
  "/quickstart": "quickstart",
  "/logs": "logs",
  "/users": "users",
  "/keys": "keys",
  "/policy": "policy",
  "/sign-in-page": "signin-page",
  "/team": "team",
  "/billing": "billing",
  "/settings": "settings",
};

/** Map a server-side onboarding `step` to its SPA route. */
const STEP_PATH: Record<OnboardingStep, string> = {
  survey: "/onboarding/survey",
  explainer: "/onboarding/explainer",
  generate_key: "/onboarding/generate-key",
  done: "/onboarding/done",
};

/**
 * Wraps the authed area. Probes `/api/me`; if no session, redirects to
 * `/login`. While the probe is in flight we show a tiny placeholder so the
 * shell doesn't flash unstyled content.
 *
 * Also enforces the onboarding wizard guard: if the signed-in operator has a
 * `currentProviderId` AND that provider's `onboarding_state.completedAt` and
 * `skippedAt` are both null, redirect to the wizard's current step. The
 * server is the source of truth — we read `step` from `/api/onboarding`.
 */
function RequireAuth({ children }: { children: ReactNode }) {
  const session = useQuery({
    queryKey: ["session"],
    queryFn: getSession,
    retry: false,
    staleTime: 60_000,
  });

  // Only fetch onboarding state once a provider is in scope. Without one the
  // user is heading to /welcome anyway.
  const onboarding = useQuery({
    queryKey: ["onboarding"],
    queryFn: apiClient.onboarding.get,
    enabled: Boolean(session.data?.currentProviderId),
    retry: false,
    staleTime: 30_000,
  });

  if (session.isLoading) {
    return <Splash />;
  }

  if (session.isError || !session.data?.user) {
    return <Navigate to="/login" replace />;
  }

  // Signed in but no active provider → the operator has zero `team_member`
  // rows. Send them to /welcome.
  if (!session.data.currentProviderId) {
    return <Navigate to="/welcome" replace />;
  }

  // Still loading the onboarding state for the first time — don't flash the
  // dashboard, but don't redirect either; we'd potentially bounce back if
  // onboarding is already done.
  if (onboarding.isLoading) {
    return <Splash />;
  }

  // Onboarding not yet terminal → route into the wizard at the current step.
  // Viewers stay on the dashboard: every wizard write (survey, key mint,
  // step PATCH) is owner/admin-gated server-side, so the wizard would just
  // trap them in 403s (#53).
  const state = onboarding.data;
  const canRunWizard =
    session.data.role === "owner" || session.data.role === "admin";
  if (state && !state.completedAt && !state.skippedAt && canRunWizard) {
    const target = STEP_PATH[state.step ?? "survey"] ?? STEP_PATH.survey;
    return <Navigate to={target} replace />;
  }

  return <>{children}</>;
}

function Splash() {
  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        color: "var(--ad-muted, #6b7280)",
        fontSize: 13,
      }}
    >
      Loading…
    </div>
  );
}

/**
 * Standalone gate for the onboarding routes. Bounces unauthed → /login,
 * no-provider → /welcome, and already-terminal → /. This keeps the wizard
 * idempotent: hitting `/onboarding/survey` directly after completion lands
 * the operator on the dashboard rather than a stuck wizard.
 */
function RequireOnboarding({ children }: { children: ReactNode }) {
  const session = useQuery({
    queryKey: ["session"],
    queryFn: getSession,
    retry: false,
    staleTime: 60_000,
  });
  const onboarding = useQuery({
    queryKey: ["onboarding"],
    queryFn: apiClient.onboarding.get,
    enabled: Boolean(session.data?.currentProviderId),
    retry: false,
    staleTime: 30_000,
  });

  if (session.isLoading) return <Splash />;
  if (session.isError || !session.data?.user) {
    return <Navigate to="/login" replace />;
  }
  if (!session.data.currentProviderId) {
    return <Navigate to="/welcome" replace />;
  }
  // Viewers can't act on any wizard step (writes are owner/admin-gated) —
  // send them to the dashboard instead of a wall of 403s (#53).
  if (session.data.role === "viewer") {
    return <Navigate to="/" replace />;
  }
  if (onboarding.isLoading) return <Splash />;
  if (onboarding.data?.completedAt || onboarding.data?.skippedAt) {
    return <Navigate to="/" replace />;
  }
  return <>{children}</>;
}

function AuthedShell() {
  const location = useLocation();
  const mainRef = useRef<HTMLElement>(null);

  useEffect(() => {
    mainRef.current?.scrollTo({ top: 0 });
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [location.pathname]);

  const screenLabel = SCREEN_LABEL[location.pathname] ?? "dashboard";

  return (
    <div className="ad-shell">
      <Sidebar />
      <Topbar />
      <main ref={mainRef} className="ad-main" data-screen-label={screenLabel}>
        <Routes>
          <Route path="/" element={<Dashboard />} />
          <Route path="/quickstart" element={<Quickstart />} />
          <Route path="/logs" element={<Logs />} />
          <Route path="/users" element={<Users />} />
          <Route path="/keys" element={<Keys />} />
          <Route path="/policy" element={<Policy />} />
          <Route path="/sign-in-page" element={<SigninPage />} />
          {/* Brand identity and Hosted pages were merged into Sign-in page. */}
          <Route path="/branding" element={<Navigate to="/sign-in-page" replace />} />
          <Route path="/hosted" element={<Navigate to="/sign-in-page" replace />} />
          <Route path="/team" element={<Team />} />
          <Route path="/billing" element={<Billing />} />
          <Route path="/settings" element={<Settings />} />
        </Routes>
      </main>
    </div>
  );
}

export default function App() {
  return (
    <Routes>
      {/* Public-ish routes outside <RequireAuth>:
            - /login: sign-in surface
            - /welcome: signed-in operators with zero memberships; on submit
              they're routed straight to /onboarding/survey (no more
              one-time `sk_` reveal — see task #24).
            - /onboarding/*: the four-step wizard. Each route is gated by
              <RequireOnboarding> which redirects to /login (no session),
              /welcome (no provider) or / (already terminal).
            - /invitations/:token: accept-invite landing. The screen handles
              the "must be signed in" branch internally — if there's no
              session it points the user to /login then bounces back. */}
      <Route path="/login" element={<Login />} />
      <Route path="/welcome" element={<Welcome />} />
      {/* Create ANOTHER org for an existing operator. Outside <RequireAuth>
          (self-gates on session) so an operator who already has a provider
          isn't bounced to their dashboard the way /welcome does. */}
      <Route path="/organizations/new" element={<NewOrganization />} />
      <Route
        path="/onboarding/survey"
        element={
          <RequireOnboarding>
            <Survey />
          </RequireOnboarding>
        }
      />
      <Route
        path="/onboarding/explainer"
        element={
          <RequireOnboarding>
            <Explainer />
          </RequireOnboarding>
        }
      />
      <Route
        path="/onboarding/generate-key"
        element={
          <RequireOnboarding>
            <GenerateKey />
          </RequireOnboarding>
        }
      />
      <Route
        path="/onboarding/done"
        element={
          <RequireOnboarding>
            <OnboardingDone />
          </RequireOnboarding>
        }
      />
      <Route path="/invitations/:token" element={<Invitation />} />
      <Route
        path="/*"
        element={
          <RequireAuth>
            <AuthedShell />
          </RequireAuth>
        }
      />
    </Routes>
  );
}
